import ActionTypes from './../common/ActionTypes';
import { uiActions } from './index';
import Strings from '../common/Strings';
import Constant from '../common/Constant';
import Analytics from '../analytics/Analytics';
import Events from '../analytics/Events';
import { sortWorkLogsByDate } from '../helpers/sort';

const firebase = require('firebase/app');
require('firebase/auth');
require('firebase/firestore');

const moment = require('moment');

let projectsFetched = false;
let projectsSnapshot;

function setLoading(isProjectsLoading) {
  return {
    type: ActionTypes.PROJECTS_LOADING,
    isProjectsLoading,
  };
}

function setFailed(isFailed) {
  return {
    type: ActionTypes.PROJECTS_FAILED,
    isFailed,
  };
}

function projectCreated(project) {
  return {
    type: ActionTypes.PROJECT_CREATED,
    project,
  };
}

function projectDeleted(project) {
  return {
    type: ActionTypes.PROJECT_DELETED,
    project,
  };
}

function projectUpdated(project) {
  return {
    type: ActionTypes.PROJECT_UPDATED,
    project,
  };
}

function clearProjectDetailsWorkLogs() {
  return {
    type: ActionTypes.PROJECT_DETAILS_WORK_LOG_CLEAR,
  };
}

function projectDetailsWorkLogsFetched(workLogs) {
  return {
    type: ActionTypes.PROJECT_DETAILS_WORK_LOG_FETCHED,
    workLogs,
  };
}

function setUpdating(isProjectUpdating) {
  return {
    type: ActionTypes.PROJECT_UPDATING,
    isProjectUpdating,
  };
}

function fetchProjects() {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      projectsFetched = false;
      dispatch(setLoading(true));
      dispatch(setFailed(false));
      stopSync();

      projectsSnapshot = firebase
        .firestore()
        .collection('projects')
        .where('ownerUid', '==', currentUser.uid)
        .onSnapshot(
          snapshot => {
            snapshot.docChanges().forEach(change => {
              const changeType = change.type;
              const project = change.doc.data();
              project.id = change.doc.id;

              if (changeType === 'added') {
                dispatch(projectCreated(project));
              } else if (changeType === 'modified') {
                dispatch(projectUpdated(project));
              } else if (changeType === 'removed') {
                dispatch(projectDeleted(project));
              }
            });

            if (!projectsFetched) {
              dispatch(setLoading(false));
              dispatch(setFailed(false));
            }

            projectsFetched = true;
          },
          () => {
            if (!projectsFetched) {
              dispatch(setLoading(false));
              dispatch(setFailed(true));
            }
          },
        );
    }
  };
}

function stopSync() {
  if (projectsSnapshot) {
    projectsSnapshot();
  }
}

function fetchProjectWorkLog(projectId) {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      // dispatch(setLoadingProjectWorkLogs(true));
      // dispatch(setProjectFailed(false));
      dispatch(clearProjectDetailsWorkLogs());

      firebase
        .firestore()
        .collection('workLogs')
        .where('ownerUid', '==', currentUser.uid)
        .where('projectId', '==', projectId)
        .get()
        .then(snapshot => {
          const workLogs = [];
          snapshot.forEach(doc => {
            const w = doc.data();
            w.id = doc.id;

            workLogs.push(w);
          });

          sortWorkLogsByDate(workLogs);

          dispatch(projectDetailsWorkLogsFetched(workLogs));
          // dispatch(setLoadingProjectWorkLogs(false));
          // dispatch(setProjectFailed(false));
        })
        .catch(() => {
          // dispatch(setLoadingProjectWorkLogs(false));
          // dispatch(setProjectFailed(true));
        });
    }
  };
}

export function createProject(project) {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      dispatch(setUpdating(true));

      project.createdAt = moment(new Date()).format(Constant.DATE_FORMAT_SHORT);
      project.archivedAt = '';
      project.ownerUid = currentUser.uid;

      Analytics.logEvent(Events.PROJECT_CREATE, {
        name: project.name,
        notes: project.notes,
        hourlyRate: project.hourlyRate,
        currencyCode: project.currencyCode,
      });

      const ref = firebase
        .firestore()
        .collection('projects')
        .doc();
      ref
        .set(project)
        .then(() => {
          dispatch(uiActions.addProjectClicked(false));
          dispatch(setUpdating(false));
        })
        .catch(() => {
          dispatch(setUpdating(false));
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.defaultBody,
            ),
          );
        });
    }
  };
}

function updateProject(project, runOnSuccess) {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      dispatch(setUpdating(true));

      const firestore = firebase.firestore();
      const projectRef = firestore.collection('projects').doc(project.id);

      firestore
        .runTransaction(async transaction => {
          transaction.set(projectRef, project, { merge: true });
        })
        .then(() => {
          dispatch(projectUpdated(project));
          dispatch(setUpdating(false));

          if (runOnSuccess) {
            runOnSuccess();
          }
        })
        .catch(() => {
          dispatch(setUpdating(false));
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.defaultBody,
            ),
          );
        });
    }
  };
}

function deleteProject(project) {
  return dispatch => {
    const { currentUser } = firebase.auth();

    if (currentUser) {
      dispatch(setUpdating(true));

      const firestore = firebase.firestore();
      const projectQuery = firestore.collection('projects').doc(project.id);
      const workLogsQuery = firestore
        .collection('workLogs')
        .where('ownerUid', '==', currentUser.uid)
        .where('projectId', '==', project.id);

      Analytics.logEvent(Events.PROJECT_DELETE, {
        name: project.name,
        notes: project.notes,
        hourlyRate: project.hourlyRate,
        currencyCode: project.currencyCode,
      });

      firestore
        .runTransaction(async transaction => {
          const d = await projectQuery.get();
          transaction.delete(d.ref);

          const workLogs = await workLogsQuery.get();
          workLogs.forEach(doc => {
            transaction.delete(doc.ref);
          });
        })
        .then(() => {
          dispatch(setUpdating(false));
        })
        .catch(() => {
          dispatch(setUpdating(false));
          dispatch(
            uiActions.showError(
              Strings.ERROR.defaultTitle,
              Strings.ERROR.defaultBody,
            ),
          );
        });
    }
  };
}

function getProjectsAvailableToLinkForCompany(uid) {
  return firebase
    .firestore()
    .collection('projects')
    .where('ownerUid', '==', uid)
    .where('archivedAt', '==', '')
    .get();
}

export const projectActions = {
  fetchProjects,
  stopSync,
  fetchProjectWorkLog,
  createProject,
  updateProject,
  deleteProject,
  getProjectsAvailableToLinkForCompany,
};



// WEBPACK FOOTER //
// ./src/actions/project.js